import axios from 'axios';
import type { Query, QueryClient, QueryKey } from '@tanstack/react-query';
import { canReadMasterCatalog, readPermissionsForMasterQuery } from './masterDataAccess';
import { MASTER_DATA_QUERY_PREFIX, type MasterDataResource } from './masterDataResources';

/** Bump when persisted catalog row shape changes (e.g. journal `usesAssistantRings`). */
export const MASTER_DATA_STORAGE_VERSION = 2;
const STORAGE_PREFIX = 'dentos.master.v1';

export type MasterDataScope = {
  organizationId: string;
  userId: string;
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

type StoredMasterData = {
  v: number;
  data: unknown;
};

const PERSISTED_PREFIXES = Object.values(MASTER_DATA_QUERY_PREFIX);

/**
 * Prefixes whose every non-search query is a stable catalog.
 * Paginated drugs/templates and partner search stay on the default stale time.
 */
const INFINITE_PREFIXES: readonly (readonly string[])[] = PERSISTED_PREFIXES.filter(
  (prefix) =>
    prefix[0] !== 'prescription' &&
    !(prefix[0] === 'labo' && prefix[1] === 'partners')
);

let scope: MasterDataScope | null = null;
let persistenceInstalled = false;
/** User who started the fetch. A late response must not be saved under the next account. */
const fetchedFor = new WeakMap<Query, MasterDataScope>();

const defaultStorage = (): StorageLike | null => {
  if (typeof localStorage === 'undefined') return null;
  return localStorage;
};

export const setMasterDataScope = (next: MasterDataScope | null): void => {
  const organizationId = next?.organizationId?.trim() ?? '';
  const userId = next?.userId?.trim() ?? '';
  if (!organizationId || !userId || userId.includes(':')) {
    scope = null;
    return;
  }
  scope = { organizationId, userId };
};

export const masterDataStorageKey = (orgId: string, userId: string, queryKey: QueryKey): string =>
  `${STORAGE_PREFIX}:${orgId}:${userId}:${encodeURIComponent(JSON.stringify(queryKey))}`;

const storageKeys = (storage: StorageLike): string[] => {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key) keys.push(key);
  }
  return keys;
};

/** Org-only keys from before the user segment. Encoded query keys start with `%5B` (`[`). */
export const removeLegacyOrgMasterData = (orgId: string, storage: StorageLike): void => {
  const prefix = `${STORAGE_PREFIX}:${orgId}:`;
  for (const storageKey of storageKeys(storage)) {
    if (!storageKey.startsWith(prefix)) continue;
    const rest = storageKey.slice(prefix.length);
    if (rest.startsWith('%5B')) storage.removeItem(storageKey);
  }
};

const keyStartsWith = (queryKey: readonly unknown[], prefix: readonly string[]): boolean =>
  prefix.every((part, index) => queryKey[index] === part);

const filtersOf = (queryKey: readonly unknown[]): Record<string, unknown> | null => {
  const last = queryKey[queryKey.length - 1];
  if (!last || typeof last !== 'object' || Array.isArray(last)) return null;
  return last as Record<string, unknown>;
};

/** Search pages and paginated tables are not the long-lived catalog copy. */
export const isPersistedMasterQuery = (queryKey: QueryKey): boolean => {
  const key = queryKey as readonly unknown[];
  if (!PERSISTED_PREFIXES.some((prefix) => keyStartsWith(key, prefix))) return false;
  const filters = filtersOf(key);
  if (!filters) return true;
  if (typeof filters.page === 'number') return false;
  if (typeof filters.search === 'string' && filters.search.trim()) return false;
  if (typeof filters.q === 'string' && filters.q.trim()) return false;
  return true;
};

const readStorage = (storage: StorageLike, key: string): unknown | undefined => {
  const raw = storage.getItem(key);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as StoredMasterData;
    if (parsed?.v !== MASTER_DATA_STORAGE_VERSION) {
      storage.removeItem(key);
      return undefined;
    }
    return parsed.data;
  } catch {
    storage.removeItem(key);
    return undefined;
  }
};

export const writeMasterDataEntry = (
  orgId: string,
  userId: string,
  queryKey: QueryKey,
  data: unknown,
  storage: StorageLike | null = defaultStorage()
): void => {
  if (!storage || !userId || userId.includes(':') || !isPersistedMasterQuery(queryKey)) return;
  try {
    const payload: StoredMasterData = { v: MASTER_DATA_STORAGE_VERSION, data };
    storage.setItem(masterDataStorageKey(orgId, userId, queryKey), JSON.stringify(payload));
  } catch {
    // Quota or private mode: memory cache still serves this session.
  }
};

export const removeMasterDataEntry = (
  orgId: string,
  userId: string,
  queryKey: QueryKey,
  storage: StorageLike | null = defaultStorage()
): void => {
  storage?.removeItem(masterDataStorageKey(orgId, userId, queryKey));
};

export const hydrateMasterData = (
  queryClient: QueryClient,
  orgId: string,
  userId: string,
  storage: StorageLike | null = defaultStorage()
): QueryKey[] => {
  if (!storage || !userId) return [];
  removeLegacyOrgMasterData(orgId, storage);
  const prefix = `${STORAGE_PREFIX}:${orgId}:${userId}:`;
  const restored: QueryKey[] = [];
  for (const storageKey of storageKeys(storage)) {
    if (!storageKey.startsWith(prefix)) continue;
    const encoded = storageKey.slice(prefix.length);
    let queryKey: QueryKey;
    try {
      queryKey = JSON.parse(decodeURIComponent(encoded)) as QueryKey;
    } catch {
      storage.removeItem(storageKey);
      continue;
    }
    if (!isPersistedMasterQuery(queryKey)) continue;
    const data = readStorage(storage, storageKey);
    if (data === undefined) continue;
    queryClient.setQueryData(queryKey, data);
    restored.push(queryKey);
  }
  return restored;
};

export const clearMasterDataMemory = (queryClient: QueryClient): void => {
  for (const prefix of PERSISTED_PREFIXES) {
    queryClient.removeQueries({ queryKey: [...prefix] });
  }
};

const isForbiddenError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 403;

/** Drop a catalog the current user is not allowed to keep in this browser. */
export const discardForbiddenMasterData = (
  queryClient: QueryClient,
  queryKey: QueryKey,
  error: unknown,
  storage: StorageLike | null = defaultStorage()
): void => {
  if (!scope || !isForbiddenError(error) || !isPersistedMasterQuery(queryKey)) return;
  removeMasterDataEntry(scope.organizationId, scope.userId, queryKey, storage);
  const query = queryClient.getQueryCache().find({ queryKey, exact: true });
  query?.setState({
    data: undefined,
    dataUpdatedAt: 0,
    status: 'error',
    fetchStatus: 'idle',
  });
};

export type MasterDataReader = (required: readonly string[]) => boolean;

export const discardUnreadableMasterData = (
  queryClient: QueryClient,
  orgId: string,
  userId: string,
  canRead: MasterDataReader,
  storage: StorageLike | null = defaultStorage(),
  queryKeys?: QueryKey[]
): void => {
  const keys =
    queryKeys ??
    queryClient
      .getQueryCache()
      .getAll()
      .map((query) => query.queryKey)
      .filter((queryKey) => isPersistedMasterQuery(queryKey));
  for (const queryKey of keys) {
    const required = readPermissionsForMasterQuery(queryKey);
    if (!required || canRead(required)) continue;
    removeMasterDataEntry(orgId, userId, queryKey, storage);
    queryClient.removeQueries({ queryKey, exact: true });
  }
};

export const masterDataReaderForUser = (
  role: string | undefined,
  permissions: string[] | undefined
): MasterDataReader => (required) => canReadMasterCatalog(role, permissions, required);

export const installMasterDataPersistence = (
  queryClient: QueryClient,
  storage?: StorageLike
): void => {
  const target = storage ?? defaultStorage();
  if (persistenceInstalled || !target) return;
  persistenceInstalled = true;
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated') return;
    const query = event.query as Query;
    if (event.action.type === 'fetch') {
      if (scope) fetchedFor.set(query, scope);
      return;
    }
    const started = fetchedFor.get(query);
    if (
      !started ||
      scope?.organizationId !== started.organizationId ||
      scope?.userId !== started.userId
    ) {
      return;
    }
    if (event.action.type === 'error') {
      const error = event.action.error;
      queueMicrotask(() => {
        if (
          scope?.organizationId !== started.organizationId ||
          scope?.userId !== started.userId
        ) {
          return;
        }
        discardForbiddenMasterData(queryClient, query.queryKey, error, target);
      });
      return;
    }
    if (event.action.type !== 'success') return;
    if (query.state.data === undefined) return;
    writeMasterDataEntry(started.organizationId, started.userId, query.queryKey, query.state.data, target);
  });
};

export const applyMasterDataQueryDefaults = (queryClient: QueryClient): void => {
  const infinite = { staleTime: Infinity, gcTime: Infinity } as const;
  for (const prefix of INFINITE_PREFIXES) {
    queryClient.setQueryDefaults([...prefix], infinite);
  }
  queryClient.setQueryDefaults(
    ['prescription', 'drugs', { limit: 1000, isActive: true }],
    infinite
  );
  queryClient.setQueryDefaults(
    ['prescription', 'templates', { isActive: true, limit: 1000 }],
    infinite
  );
  queryClient.setQueryDefaults(['labo', 'partners', 'active'], infinite);
};

export const masterDataPrefixForResource = (
  resource: string
): readonly string[] | undefined =>
  MASTER_DATA_QUERY_PREFIX[resource as MasterDataResource];
